// 프로젝트 공유(owner 전용) — 사용자·그룹에게 읽기/편집 권한을 준다.
// 백엔드(역할 viewer/editor/owner, 권한 매트릭스, 그룹 공유)는 처음부터 있었는데
// 화면 배선이 없어 owner 밖에 존재하지 않았다(마스터 검수 2026-08-14).
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconUsers, IconX } from '@tabler/icons-react';
import { projectAPI } from '../../api/client';
import { alertDialog, confirmDialog } from '../../components/AppDialog';
import { COLORS } from '../../utils/uiStyles';

const ROLE_LABEL = { viewer: '읽기', editor: '편집', owner: '소유자' };
const KIND_LABEL = { user: '', group: '그룹' };

const box = {
  width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px',
  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
};

export default function ProjectShareSection({ projectId, role }) {
  const [shares, setShares] = useState([]);
  const [kind, setKind] = useState('user');       // user | group
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [newRole, setNewRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const seqRef = useRef(0);

  const isOwner = role === 'owner';

  const load = useCallback(async () => {
    if (!projectId || !isOwner) return;
    try {
      const r = await projectAPI.listShares(projectId);
      setShares(Array.isArray(r.data) ? r.data : []);
      setErr('');
    } catch {
      setShares([]);
      setErr('공유 목록을 불러오지 못했습니다.');
    }
  }, [projectId, isOwner]);

  useEffect(() => { load(); }, [load]);

  // 검색은 입력이 멎은 뒤에만(글자마다 호출 방지). 늦게 온 응답은 버린다.
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return undefined; }
    const seq = ++seqRef.current;
    const t = setTimeout(async () => {
      try {
        const r = kind === 'group'
          ? await projectAPI.searchGroups(term)
          : await projectAPI.searchUsers(term);
        if (seq === seqRef.current) setResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (seq === seqRef.current) setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, kind]);

  if (!isOwner) return null;

  const add = async (target) => {
    setBusy(true);
    try {
      const r = await projectAPI.addShare(projectId, {
        principal_kind: kind, principal_id: target.id, role: newRole,
      });
      setShares(Array.isArray(r.data) ? r.data : []);
      setQ('');
      setResults([]);
    } catch (e) {
      alertDialog(e.response?.data?.detail || '공유에 실패했습니다.');
    } finally { setBusy(false); }
  };

  const changeRole = async (row, nextRole) => {
    setBusy(true);
    try {
      const r = await projectAPI.addShare(projectId, {
        principal_kind: row.principal_kind, principal_id: row.principal_id, role: nextRole,
      });
      setShares(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      alertDialog(e.response?.data?.detail || '권한 변경에 실패했습니다.');
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    const who = row.name || row.email || '이 대상';
    if (!(await confirmDialog(`${who}의 공유를 해제하시겠습니까?`))) return;
    setBusy(true);
    try {
      await projectAPI.removeShare(projectId, row.principal_kind, row.principal_id);
      await load();
    } catch (e) {
      alertDialog(e.response?.data?.detail || '공유 해제에 실패했습니다.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <IconUsers size={15} stroke={1.8} aria-hidden="true" />
        <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>공유</label>
      </div>
      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
        읽기는 대화·파일 열람만, 편집은 파일 추가·수정까지 가능합니다.
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
        <select value={kind} onChange={(e) => { setKind(e.target.value); setResults([]); setQ(''); }}
          style={{ ...box, width: '90px', flexShrink: 0 }}>
          <option value="user">사용자</option>
          <option value="group">그룹</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={kind === 'group' ? '그룹 이름 검색' : '이름 또는 이메일 검색'}
          style={box} />
        <select value={newRole} onChange={(e) => setNewRole(e.target.value)}
          style={{ ...box, width: '86px', flexShrink: 0 }}>
          <option value="viewer">읽기</option>
          <option value="editor">편집</option>
        </select>
      </div>

      {results.length > 0 && (
        <div style={{ border: '1px solid #eef0f3', borderRadius: '8px', marginBottom: '10px', maxHeight: '150px', overflowY: 'auto' }}>
          {results.map((u) => (
            <button key={`${kind}-${u.id}`} type="button" disabled={busy} onClick={() => add(u)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none',
                borderBottom: '1px solid #f5f6f8', background: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: '13px' }}>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>{u.name}</span>
              {u.email && <span style={{ color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>{u.email}</span>}
            </button>
          ))}
        </div>
      )}

      {err && <div style={{ fontSize: '12px', color: COLORS.danger || '#ef4444', marginBottom: '6px' }}>{err}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {shares.length === 0 && (
          <div style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>아직 공유한 사람이 없습니다.</div>
        )}
        {shares.map((s) => (
          <div key={`${s.principal_kind}-${s.principal_id}`}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px' }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {KIND_LABEL[s.principal_kind] && (
                <span style={{ fontSize: '11px', color: '#0369a1', background: '#e0f2fe', borderRadius: '6px', padding: '1px 6px', marginRight: '6px' }}>
                  {KIND_LABEL[s.principal_kind]}
                </span>
              )}
              <span style={{ fontWeight: 600, color: '#1f2937' }}>{s.name}</span>
              {s.email && <span style={{ color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>{s.email}</span>}
            </span>
            <select value={s.role} disabled={busy} onChange={(e) => changeRole(s, e.target.value)}
              style={{ ...box, width: '82px', padding: '5px 8px', flexShrink: 0 }}>
              <option value="viewer">{ROLE_LABEL.viewer}</option>
              <option value="editor">{ROLE_LABEL.editor}</option>
            </select>
            <button type="button" onClick={() => remove(s)} disabled={busy}
              title="공유 해제" aria-label="공유 해제"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: busy ? 'default' : 'pointer', display: 'inline-flex', flexShrink: 0 }}>
              <IconX size={15} stroke={2} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
